<template>
  <div class="supply-demand-panel">
    <div class="sd-header">
      <span>水量供需平衡分析</span>
      <button class="sd-close" @click="$emit('close')">×</button>
    </div>

    <div v-if="query" class="sd-query">
      <div>
        <strong>{{ query.station.name }}</strong>
        <span>演示供需测算</span>
      </div>
      <p>可供水量 {{ query.values.supply }} 万m³/d，需水量 {{ query.values.demand }} 万m³/d，{{ queryBalanceText }}。</p>
    </div>

    <div class="sd-section">
      <div class="sd-section-title">实时供需概览</div>
      <div class="sd-summary">
        <div class="sd-card">
          <div class="sd-card-value">{{ totalSupply }}</div>
          <div class="sd-card-label">可供水量（亿m³）</div>
          <div class="sd-card-sub">来自地表水/地下水/调水/再生水</div>
        </div>
        <div class="sd-card shortage">
          <div class="sd-card-value">{{ totalDemand }}</div>
          <div class="sd-card-label">总需水量（亿m³）</div>
          <div class="sd-card-sub">城市/工业/农业/生态/农村</div>
        </div>
        <div class="sd-card" :class="balanceClass">
          <div class="sd-card-value">{{ balance >= 0 ? '+' : '' }}{{ balance.toFixed(1) }}</div>
          <div class="sd-card-label">{{ balance >= 0 ? '盈余' : '缺额' }}（亿m³）</div>
          <div class="sd-card-sub">{{ balance >= 0 ? '供需平衡' : '触发缺水预警' }}</div>
        </div>
      </div>
    </div>

    <div class="sd-section">
      <div class="sd-section-title">分区供需明细</div>
      <div class="sd-table-wrap">
        <table class="sd-table">
          <thead>
            <tr><th>分区</th><th>可供水量(万m³)</th><th>需水量(万m³)</th><th>平衡</th><th>状态</th></tr>
          </thead>
          <tbody>
            <tr v-for="r in regions" :key="r.name">
              <td>{{ r.name }}</td>
              <td>{{ r.supply }}</td>
              <td>{{ r.demand }}</td>
              <td :class="r.supply >= r.demand ? 'positive' : 'negative'">{{ (r.supply - r.demand).toFixed(0) }}</td>
              <td><span class="sd-tag" :class="r.supply >= r.demand ? 'ok' : 'warn'">{{ r.supply >= r.demand ? '平衡' : '缺水' }}</span></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="sd-section">
      <div class="sd-section-title">供需对比图</div>
      <div ref="chartEl" class="sd-chart"></div>
    </div>

    <div class="sd-actions">
      <button @click="$emit('openDispatch')">生成供水调度建议</button>
      <span>按当前缺口生成运行调度任务和水量分配建议。</span>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { fetchSupplyDemandRegions, toSupplyDemandRows } from '@/services/businessDataService.js'

export default {
  name: 'SupplyDemandPanel',
  props: {
    query: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      regions: [
        { name: '延安市宜川', supply: 18250, demand: 17400 },
        { name: '渭南市辖区', supply: 45200, demand: 48600 },
        { name: '咸阳市辖区', supply: 8900, demand: 7700 },
        { name: '西安市辖区', supply: 12800, demand: 13500 },
        { name: '工业园区A区', supply: 18600, demand: 19700 },
        { name: '工业园区B区', supply: 11200, demand: 10800 },
        { name: '东雷灌区', supply: 28000, demand: 29000 },
      ]
    }
  },
  created() {
    if (this.query && this.query.station && this.query.values) {
      const stationRegion = {
        name: this.query.station.name,
        supply: Math.round(this.query.values.supply * 100),
        demand: Math.round(this.query.values.demand * 100),
      }
      this.regions = [stationRegion, ...this.regions.filter(r => r.name !== stationRegion.name)].slice(0, 7)
    }
  },
  computed: {
    totalSupply() { return (this.regions.reduce((s, r) => s + r.supply, 0) / 10000).toFixed(1) },
    totalDemand() { return (this.regions.reduce((s, r) => s + r.demand, 0) / 10000).toFixed(1) },
    balance() { return parseFloat(this.totalSupply) - parseFloat(this.totalDemand) },
    balanceClass() { return this.balance >= 0 ? 'surplus' : 'deficit' },
    queryBalanceText() {
      if (!this.query || !this.query.values) return ''
      return this.query.values.deficit > 0 ? `缺额 ${this.query.values.deficit} 万m³/d` : '当前供需平衡'
    }
  },
  mounted() {
    this.loadSupplyDemandData()
  },
  methods: {
    async loadSupplyDemandData() {
      const payload = await fetchSupplyDemandRegions()
      const rows = toSupplyDemandRows(payload.regions || [])
      if (rows.length) {
        this.regions = rows
        if (this.query && this.query.station && this.query.values) {
          const stationRegion = {
            name: this.query.station.name,
            supply: Math.round(this.query.values.supply * 100),
            demand: Math.round(this.query.values.demand * 100),
          }
          this.regions = [stationRegion, ...this.regions.filter(r => r.name !== stationRegion.name)].slice(0, 7)
        }
      }
      this.$nextTick(() => this.renderChart())
    },
    renderChart() {
      if (!this.$refs.chartEl) return
      if (this._chart) this._chart.dispose()
      const chart = echarts.init(this.$refs.chartEl)
      chart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { data: ['可供水量', '需水量'], textStyle: { color: '#aaa' } },
        grid: { left: '10%', right: '10%', top: 40, bottom: 30 },
        xAxis: { type: 'category', data: this.regions.map(r => r.name), axisLabel: { color: '#888', fontSize: 10 } },
        yAxis: { type: 'value', name: '万m³', nameTextStyle: { color: '#888' }, axisLabel: { color: '#888' } },
        series: [
          { name: '可供水量', type: 'bar', data: this.regions.map(r => r.supply), itemStyle: { color: '#4a9eff' } },
          { name: '需水量', type: 'bar', data: this.regions.map(r => r.demand), itemStyle: { color: '#ff6b6b' } },
        ]
      })
      this._chart = chart
    }
  },
  beforeDestroy() {
    if (this._chart) this._chart.dispose()
  }
}
</script>

<style scoped>
.supply-demand-panel {
position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
width: 680px;
background: linear-gradient(135deg, rgba(8, 18, 32, 0.96), rgba(13, 38, 50, 0.94));
border: 1px solid rgba(99, 179, 237, 0.28);
border-radius: 10px;
backdrop-filter: blur(20px);
box-shadow: 0 18px 52px rgba(0, 0, 0, 0.42);
z-index: 1000;
  color: #fff;
  max-height: 90vh;
  overflow-y: auto;
}
.sd-header { display: flex; justify-content: space-between; align-items: center; padding: 14px 20px; border-bottom: 1px solid rgba(255,255,255,0.08); font-size: 15px; font-weight: 600; }
.sd-close { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); color: rgba(255,255,255,0.72); font-size: 16px; width: 30px; height: 30px; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 0.2s, border-color 0.2s; }
.sd-close:hover { background: rgba(255,255,255,0.12); color: #fff; }
.sd-query { margin: 14px 20px 0; padding: 12px 14px; border: 1px solid rgba(56,189,248,0.16); border-radius: 7px; background: rgba(56,189,248,0.08); }
.sd-query div { display: flex; align-items: center; gap: 10px; }
.sd-query strong { color: #8bc5ff; font-size: 16px; }
.sd-query span, .sd-query p { color: rgba(255,255,255,0.72); font-size: 12px; margin: 0; }
.sd-query p { margin-top: 6px; }
.sd-section { padding: 16px 20px; }
.sd-section-title { font-size: 13px; color: rgba(255,255,255,0.6); margin-bottom: 12px; letter-spacing: 0.04em; }
.sd-summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.sd-card { background: rgba(255,255,255,0.045); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 16px; text-align: center; }
.sd-card.surplus { border-color: rgba(52,211,153,0.3); }
.sd-card.deficit { border-color: rgba(255,107,107,0.4); }
.sd-card-value { font-size: 24px; font-weight: 700; margin-bottom: 4px; }
.sd-card.deficit .sd-card-value { color: #ff6b6b; }
.sd-card.surplus .sd-card-value { color: #34d399; }
.sd-card-label { font-size: 12px; color: rgba(255,255,255,0.7); margin-bottom: 4px; }
.sd-card-sub { font-size: 11px; color: rgba(255,255,255,0.4); }
.sd-table-wrap { overflow-x: auto; }
.sd-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.sd-table th { text-align: left; padding: 10px 12px; border-bottom: 1px solid rgba(56,189,248,0.16); color: #b8ecff; font-weight: 600; }
.sd-table td { padding: 10px 12px; border-bottom: 1px solid rgba(255,255,255,0.04); color: rgba(255,255,255,0.8); }
.positive { color: #34d399; }
.negative { color: #ff6b6b; }
.sd-tag { padding: 2px 8px; border-radius: 10px; font-size: 11px; }
.sd-tag.ok { background: rgba(52,211,153,0.15); color: #34d399; }
.sd-tag.warn { background: rgba(255,107,107,0.15); color: #ff6b6b; }
.sd-chart { width: 100%; height: 260px; }
.sd-actions { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 0 20px 18px; }
.sd-actions button { height: 34px; padding: 0 16px; border: 1px solid rgba(56,189,248,0.38); border-radius: 8px; background: rgba(56,189,248,0.14); color: #b8ecff; cursor: pointer; font-size: 13px; }
.sd-actions button:hover { background: rgba(56,189,248,0.24); }
.sd-actions span { color: rgba(255,255,255,0.5); font-size: 12px; line-height: 1.4; }
</style>
